<template>
  <el-dialog
    :title="isEdit ? '编辑游戏' : '添加游戏'"
    :visible.sync="dialogVisible"
    width="600px"
    :before-close="handleClose"
  >
    <el-form 
      :model="form" 
      :rules="rules" 
      ref="gameForm" 
      label-width="100px"
    >
      <el-form-item label="游戏名称" prop="gameName">
        <el-input v-model="form.gameName" placeholder="请输入游戏名称"></el-input>
      </el-form-item>
      
      <el-form-item label="平台" prop="platform">
        <el-select 
          v-model="form.platformArray" 
          placeholder="请选择平台" 
          style="width: 100%"
          multiple
          @change="handlePlatformChange"
        >
          <el-option 
            v-for="platform in platformList" 
            :key="platform.id" 
            :label="platform.platform" 
            :value="platform.platform"
          ></el-option>
        </el-select>
      </el-form-item>
      
      <el-form-item label="维度" prop="dimension">
        <el-select v-model="form.dimension" placeholder="请选择维度" style="width: 100%">
          <el-option label="2D" value="2D"></el-option>
          <el-option label="3D" value="3D"></el-option>
        </el-select>
      </el-form-item>
      
      <el-form-item label="分类" prop="categoryArray">
        <el-select 
          v-model="form.categoryArray" 
          placeholder="请选择分类" 
          style="width: 100%"
          multiple
          @change="handleCategoryChange"
        >
          <el-option 
            v-for="type in gameTypeList" 
            :key="type.id" 
            :label="type.gameType" 
            :value="type.gameType"
          ></el-option>
        </el-select>
      </el-form-item>
      
      <el-form-item label="游戏库" prop="gameLibrary">
        <el-select v-model="form.gameLibrary" placeholder="请选择游戏库" style="width: 100%">
          <el-option 
            v-for="library in gameLibraryList" 
            :key="library.id" 
            :label="library.gameLibrary" 
            :value="library.gameLibrary"
          ></el-option>
        </el-select>
      </el-form-item>
      
      <el-form-item label="游戏Logo" prop="logoUrl">
        <div class="upload-section">
          <el-upload
            class="logo-uploader"
            :action="uploadUrl"
            :headers="uploadHeaders"
            :data="uploadData"
            :show-file-list="false"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            :before-upload="beforeUpload"
            name="image"
            accept="image/*"
          >
            <div v-if="form.logoUrl" class="logo-preview">
              <img :src="form.logoUrl" class="logo-image" />
              <div class="logo-overlay">
                <i class="el-icon-upload"></i>
                <p>点击更换</p>
              </div>
            </div>
            <div v-else class="logo-upload-placeholder">
              <i class="el-icon-plus"></i>
              <p>点击上传Logo</p>
            </div>
          </el-upload>
          <div v-if="form.logoUrl" class="logo-actions">
            <el-button size="mini" type="danger" @click="removeLogo">移除</el-button>
          </div>
        </div>
      </el-form-item>
      
      <el-form-item label="游戏链接">
        <el-input 
          v-model="form.gameUrl" 
          placeholder="请输入游戏访问链接"
        ></el-input>
      </el-form-item>
      
      <el-form-item label="游戏描述" prop="description">
        <el-input 
          v-model="form.description" 
          type="textarea" 
          :rows="4"
          placeholder="请输入游戏描述"
          maxlength="500"
          show-word-limit
        ></el-input>
      </el-form-item>
    </el-form>
    
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="submitting">
        {{ isEdit ? '更新' : '添加' }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'GameForm',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    gameData: {
      type: Object,
      default: null
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    platformList: {
      type: Array,
      default: () => []
    },
    gameTypeList: {
      type: Array,
      default: () => []
    },
    gameLibraryList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      form: {
        id: null,
        gameName: '',
        platform: '',
        platformArray: [], // 多选平台的数组
        dimension: '',
        logoUrl: '',
        category: '',
        categoryArray: [], // 多选分类的数组
        gameUrl: '',
        gameLibrary: '',
        description: ''
      },
      rules: {
        gameName: [
          { required: true, message: '请输入游戏名称', trigger: 'blur' },
          { min: 1, max: 100, message: '游戏名称长度在 1 到 100 个字符', trigger: 'blur' }
        ],
        platformArray: [
          { required: true, message: '请选择平台', trigger: 'change' }
        ],
        dimension: [
          { required: true, message: '请选择维度', trigger: 'change' }
        ],
        categoryArray: [
          { required: true, message: '请选择分类', trigger: 'change' }
        ],
        gameLibrary: [
          { required: true, message: '请选择游戏库', trigger: 'change' }
        ]
      },
      submitting: false,
      uploadUrl: '/api/common/upload', // 上传接口地址
      uploadHeaders: {},
      uploadData: {}
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.initForm()
      }
    },
    gameData: {
      handler(val) {
        if (val && this.visible) {
          this.$nextTick(() => {
            this.initForm()
          })
        }
      },
      deep: true,
      immediate: true
    }
  },
  // 移除mounted钩子中的loadPlatformList调用
  mounted() {
    // 平台数据现在从父组件传入，不再需要自己请求
  },
  methods: {
    // 移除平台列表加载方法
    // 初始化表单
    initForm() {
      console.log('initForm called, isEdit:', this.isEdit, 'gameData:', this.gameData)
      
      if (this.isEdit && this.gameData) {
        // 先处理平台数据
        let platformArray = []
        if (this.gameData.platform) {
          const platformOrder = this.platformList.map(p => p.platform)
          const platformArrayFromData = this.gameData.platform.split('/')
          platformArray = platformOrder.filter(platform => platformArrayFromData.includes(platform))
          console.log('Platform data:', this.gameData.platform, 'Parsed array:', platformArray)
        }
        
        // 处理分类数据
        let categoryArray = []
        if (this.gameData.category) {
          const categoryOrder = this.gameTypeList.map(t => t.gameType)
          const categoryArrayFromData = this.gameData.category.split('/')
          categoryArray = categoryOrder.filter(category => categoryArrayFromData.includes(category))
          console.log('Category data:', this.gameData.category, 'Parsed array:', categoryArray)
        }
        
        // 创建新的form对象，确保响应式更新
        this.form = {
          ...this.gameData,
          platformArray: platformArray,
          categoryArray: categoryArray
        }
        console.log('Form initialized:', this.form)
      } else {
        this.form = {
          id: null,
          gameName: '',
          platform: '',
          platformArray: [],
          dimension: '',
          logoUrl: '',
          category: '',
          categoryArray: [],
          gameUrl: '',
          gameLibrary: '',
          description: ''
        }
      }
      // 清除验证状态
      this.$nextTick(() => {
        if (this.$refs.gameForm) {
          this.$refs.gameForm.clearValidate()
        }
      })
    },

    // 处理平台选择变化
    handlePlatformChange(selectedPlatforms) {
      // 按照平台列表的顺序排列平台
      const platformOrder = this.platformList.map(p => p.platform)
      const sortedPlatforms = platformOrder.filter(platform => selectedPlatforms.includes(platform))
      this.form.platform = sortedPlatforms.join('/')
      // 更新platformArray为排序后的数组
      this.form.platformArray = sortedPlatforms
    },

    // 处理分类选择变化
    handleCategoryChange(selectedCategories) {
      // 按照分类列表的顺序排列分类
      const categoryOrder = this.gameTypeList.map(t => t.gameType)
      const sortedCategories = categoryOrder.filter(category => selectedCategories.includes(category))
      this.form.category = sortedCategories.join('/')
      // 更新categoryArray为排序后的数组
      this.form.categoryArray = sortedCategories
    },

    // 上传前检查
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isImage) {
        this.$message.error('只能上传图片文件!')
        return false
      }
      if (!isLt2M) {
        this.$message.error('上传图片大小不能超过 2MB!')
        return false
      }
      return true
    },

    // 上传成功
    handleUploadSuccess(response) {
      if (response.code === 200) {
        this.form.logoUrl = response.data
        this.$message.success('图片上传成功')
      } else {
        this.$message.error(response.msg || '图片上传失败')
      }
    },

    // 上传失败
    handleUploadError() {
      this.$message.error('图片上传失败')
    },

    // 移除Logo
    removeLogo() {
      this.form.logoUrl = ''
    },

    // 提交表单
    async handleSubmit() {
      this.$refs.gameForm.validate(async (valid) => {
        if (valid) {
          this.submitting = true
          try {
            const formData = { ...this.form }
            
            if (this.isEdit && this.gameData && this.gameData.id) {
              // 编辑模式
              formData.id = this.gameData.id
              const response = await import('../api/gameApi.js').then(module => module.gameApi.updateGame(formData))
              if (response.code === 200) {
                this.$message.success('游戏更新成功')
                // 将更新后的数据传递给父组件
                this.$emit('save', { ...formData })
              } else {
                this.$message.error(response.msg || '游戏更新失败')
              }
            } else {
              // 添加模式
              const response = await import('../api/gameApi.js').then(module => module.gameApi.insertGame(formData))
              if (response.code === 200) {
                this.$message.success('游戏添加成功')
                // 将新添加的数据传递给父组件
                this.$emit('save', { ...formData })
              } else {
                this.$message.error(response.msg || '游戏添加失败')
              }
            }
          } catch (error) {
            this.$message.error('操作失败，请重试')
          } finally {
            this.submitting = false
          }
        }
      })
    },

    // 关闭对话框
    handleClose() {
      this.dialogVisible = false
      // 延迟重置表单，避免在动画期间重置导致闪烁
      setTimeout(() => {
        this.initForm()
      }, 300)
    }
  }
}
</script>

<style scoped>
.game-form {
  margin-top: 20px;
}

.upload-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.logo-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  width: 150px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-uploader:hover {
  border-color: #409eff;
}

.logo-upload-placeholder {
  text-align: center;
  color: #999;
}

.logo-upload-placeholder i {
  font-size: 28px;
  color: #c0c4cc;
}

.logo-upload-placeholder p {
  margin-top: 12px;
  font-size: 14px;
}

.logo-preview {
  position: relative;
  width: 150px;
  height: 150px;
}

.logo-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.logo-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.logo-preview:hover .logo-overlay {
  opacity: 1;
}

.logo-actions {
  margin-top: 10px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
}

.el-textarea ::v-deep .el-textarea__inner {
  resize: vertical;
}
</style>
